import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import {
  SchemaField,
  Document,
  DocumentSchema,
  SystemFieldType,
  DocumentProximityStatus
} from 'src/app/modules/core/store/models/document.model';
import { DocumentReader } from 'src/app/modules/core/store/models/document-reader';
import { DynamicField } from '../dynamic-field';
import { ViewComponent } from '../../../store/models';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import * as fromUserProfile from 'src/app/modules/core/store/user-profile';
import { TranslateService } from '@ngx-translate/core';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';

@Component({
    selector: 'app-distance-field',
    templateUrl: './distance-field.component.html',
    styleUrls: ['./distance-field.component.scss'],
    standalone: false
})
export class DistanceFieldComponent implements OnInit, DynamicField, OnDestroy {
  @Output() valueChanged = new EventEmitter<DynamicField>();
  @Input() field: SchemaField;
  @Input() displayName: string;
  @Input() layerUId: string;
  @Input() set document(value: Document) {
    this.reader = new DocumentReader(value);
  }
  reader: DocumentReader;
  @Input() viewComponent: ViewComponent;
  isReadOnly: boolean;
  @Input() schema: DocumentSchema;
  @Input() dataset: Dataset;

  unitsConversionValue: number;
  unitAbbrv: string;

  get value(): any {
    return this._value ?? null;
  }

  set value(val: any) {
    this._value = val;
  }

  get displayValue(): any {
    if (this._value === DocumentProximityStatus.Pending || this._value === DocumentProximityStatus.Error) {
      const key = this._value === DocumentProximityStatus.Pending ? 'MANAGEMENT.DATASETS.CALCULATING' : 'MANAGEMENT.DATASETS.CALC-ERROR';
      return this.translate.instant(key);
    }

    if (this.value === 0) {
      return this.value;
    } else if (!this.value) {
      return null;
    }

    return this.value / this.unitsConversionValue;
  }

  get displayFormat(): any {
    if (this._value === DocumentProximityStatus.Pending || this._value === DocumentProximityStatus.Error) {
      return '';
    } else {
      return '00[.]00';
    }
  }

  set displayValue(val: any) {
    this.value = val * this.unitsConversionValue;
  }

  _value: any;

  showFieldAsEdit: boolean = false;
  private _isEdit: boolean = false;
  @Input() set isEdit(edit: boolean) {
    this._isEdit = edit;

    this.calculateShouldDisplayFieldForEdit();
  }

  calculateShouldDisplayFieldForEdit() {
    this.showFieldAsEdit = this._isEdit && !this.isReadOnly;
  }

  constructor(private store: Store<fromUserProfile.State>, private translate: TranslateService) {}

  ngOnInit(): void {
    this.store
      .select(fromUserProfile.selectDistanceUnit)
      .pipe(take(1))
      .subscribe(unit => {
        if (unit?.toLocaleLowerCase() === 'miles') {
          this.translate.get(`UNITS.DISTANCE.${'MILES-ABBREVIATED-LOWER'}`).subscribe((res: string) => (this.unitAbbrv = res));
          this.unitsConversionValue = 1609;
        } else {
          this.translate.get(`UNITS.DISTANCE.${'KILOMETERS-ABBREVIATED-LOWER'}`).subscribe((res: string) => (this.unitAbbrv = res));
          this.unitsConversionValue = 1000;
        }
      });
  }

  ngOnDestroy(): void {
    this.valueChanged.complete();
  }

  onValueChanged(): void {
    this.valueChanged.emit(this);
  }

  tpOnContentUpdated(): void {
    this.value = this.reader.getFieldValue(this.field?.systemName);

    if ((this.field.fieldType & SystemFieldType.Editable) !== SystemFieldType.Editable || this.viewComponent?.configuration?.isReadOnly) {
      this.isReadOnly = true;
      this.calculateShouldDisplayFieldForEdit();
    }
  }
}
